/* Copyright (c) 2021-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import {
    DefaultHTTPFailureDNSLookupCacheValidity,
    StatusCodeZeroErrorTypes
} from 'nv-angular-http-endpoint/lib/constants/constants';
import { BridgeService } from '../bridge/bridge.service';
import { NetworkStatusService } from '../network-status-service/network-status.service';
import {
    DNSLookupEntry,
    ErrorAnalysisInfo,
    FailedRequestInfo,
    DNSLookupSuccessResponse,
    UNKNOWN_ERROR,
    StatusCodeZeroAnalyzerConfig
} from './analyze-status-code-zero';

export class StatusCodeZeroAnalyzer {
    private dnsLookupMap: Map<string, DNSLookupEntry>;
    private requestCancelledMap: Map<string, boolean>;
    private dnsLookupValidity: number;

    constructor(
        private params: StatusCodeZeroAnalyzerConfig,
        private networkStatusService: NetworkStatusService,
        private bridgeService: BridgeService
    ) {
        this.dnsLookupValidity = this.params?.dnsCacheValidity ?? DefaultHTTPFailureDNSLookupCacheValidity;
        this.dnsLookupMap = new Map<string, DNSLookupEntry>();
        this.requestCancelledMap = new Map<string, boolean>();
    }

    private checkNetworkStatus(): Observable<ErrorAnalysisInfo> {
        return this.networkStatusService.isOnline
            ? of(UNKNOWN_ERROR)
            : of({ statusCode: StatusCodeZeroErrorTypes.NetworkStatusOffline });
    }

    private getCachedDNSEntry(hostname: string): DNSLookupEntry {
        const mapEntry = this.dnsLookupMap.get(hostname);
        if (mapEntry && Date.now() - mapEntry.lastUpdatedTs <= this.dnsLookupValidity) {
            return mapEntry;
        }
        return undefined;
    }

    private checkDNSLookup(requestUrl: string): Observable<ErrorAnalysisInfo> {
        const hostname = new URL(requestUrl).hostname;
        const cachedDNSEntry = this.getCachedDNSEntry(hostname);
        if (cachedDNSEntry) {
            return cachedDNSEntry.lookupSuccessful
                ? of(UNKNOWN_ERROR)
                : of({
                      statusCode: StatusCodeZeroErrorTypes.DNSLookupFailed,
                      isUserOnline: true,
                      cachedDNSEntry
                  });
        }

        return this.bridgeService
            .executeCommand(Commands.Common.Systeminfo.DNSLookup, {
                name: hostname
            })
            .pipe(
                map((remoteIP: DNSLookupSuccessResponse) => {
                    if (!remoteIP) {
                        // In case of browser client, we do not have existing or proposed implementation
                        // for resolving DNS and thus no implementation exists for the bridge command.
                        // In such cases, null is returned. As we cannot conclusively call it a DNS error,
                        // we return UNKNOWN_ERROR here. For native clients, when DNS resolution fails,
                        // exception is raised and that is handled in catchError
                        return UNKNOWN_ERROR;
                    }

                    const dnsEntry: DNSLookupEntry = {
                        lookupSuccessful: true,
                        lastUpdatedTs: Date.now(),
                        destinationIp: remoteIP?.hostname || ''
                    };
                    this.dnsLookupMap.set(hostname, dnsEntry);
                    return {
                        statusCode: StatusCodeZeroErrorTypes.UnknownError,
                        cachedDNSEntry: dnsEntry
                    };
                }),
                catchError(_ => {
                    const dnsEntry: DNSLookupEntry = {
                        lookupSuccessful: false,
                        lastUpdatedTs: Date.now(),
                        destinationIp: ''
                    };
                    this.dnsLookupMap.set(hostname, dnsEntry);
                    return of({
                        statusCode: StatusCodeZeroErrorTypes.DNSLookupFailed,
                        isUserOnline: true,
                        cachedDNSEntry: dnsEntry
                    });
                })
            );
    }

    private checkRequestCancelled(requestInfo: FailedRequestInfo): Observable<ErrorAnalysisInfo> {
        const errorInfo: ErrorAnalysisInfo = {
            statusCode: StatusCodeZeroErrorTypes.RequestCancelled
        };

        if (requestInfo.uniqueRequestId && this.requestCancelledMap.has(requestInfo.uniqueRequestId)) {
            return of(errorInfo);
        }

        if (requestInfo.isCancelled) {
            if (requestInfo.uniqueRequestId) {
                this.requestCancelledMap.set(requestInfo.uniqueRequestId, true);
            }
            return of(errorInfo);
        }

        return of(UNKNOWN_ERROR);
    }

    getFailureCode = (requestInfo: FailedRequestInfo): Observable<ErrorAnalysisInfo> =>
        this.checkRequestCancelled(requestInfo).pipe(
            mergeMap(errorAnalysis =>
                errorAnalysis.statusCode !== StatusCodeZeroErrorTypes.UnknownError
                    ? of(errorAnalysis)
                    : this.checkNetworkStatus()
            ),
            mergeMap(errorAnalysis =>
                errorAnalysis.statusCode !== StatusCodeZeroErrorTypes.UnknownError
                    ? of(errorAnalysis)
                    : this.checkDNSLookup(requestInfo.requestUrl)
            ),
            catchError(_ => of(UNKNOWN_ERROR))
        );
}
